var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
import { EDaysOfWeek, EQuantityCalculation, EQuantityCalculationInt } from './booking-calculation-price-rule-model';
export var getTicketPricingRuleBracket = function (item) {
  // the first bracket is the cost price so we don't need to set a price bracket for these quantities
  var priceBrackets = item.pricingRule.brackets.filter(function (rule) {
    return rule.from !== 1;
  });
  priceBrackets = priceBrackets.map(function (bracket) {
    return __assign(__assign({}, bracket), {
      to: bracket.to === 0 ? Infinity : bracket.to
    });
  });
  var quantity = item.pricingRule.quantityCalculation === EQuantityCalculation.perProduct || item.pricingRule.quantityCalculation === EQuantityCalculation.ticketTotal || item.pricingRule.quantityCalculation === EQuantityCalculationInt.perProduct ? item.pricingRule.perProductAmount : item.quantity;
  return priceBrackets.find(function (bracket) {
    return quantity >= bracket.from && quantity <= bracket.to;
  });
};
/**
 * Calculates the price for a booking item based on its time tier rule.
 * @param ticketTimeTiers - Array of time tiers containing pricing rules.
 * @param timeRule - The rule set containing override dates and recurring day configurations.
 * @param bookingDate - The date of the booking.
 * @param sessionStart - The start time of the session in a 24-hour integer format.
 * @returns The price based on the matching tier, or the base price if no tier matches.
 */
export var getTicketTier = function (ticketTimeTiers, timeRule, bookingDate, sessionStart) {
  var tierId = null;
  // Determine tierId by checking override dates
  var overrideDates = timeRule.overrideDates;
  var overrideDate = overrideDates.find(function (od) {
    return od.dates.some(function (dateRange) {
      var startDate = new Date(dateRange.startDate);
      startDate.setHours(0, 0, 0, 0);
      var endDate = new Date(dateRange.endDate);
      endDate.setHours(0, 0, 0, 0);
      return startDate && endDate && startDate <= bookingDate && bookingDate <= endDate;
    });
  });
  if (overrideDate) {
    var day = overrideDate.days.find(function (day) {
      return mapDayOfWeek(day.dayOfWeek) === bookingDate.getDay();
    });
    tierId = day ? getTierIdFromDate(day, sessionStart) : null;
  }
  // Check recurring days if no override date matches
  if (!tierId) {
    var recurringDay = timeRule.recurring.find(function (day) {
      return mapDayOfWeek(day.dayOfWeek) === bookingDate.getDay();
    });
    tierId = recurringDay ? getTierIdFromDate(recurringDay, sessionStart) : null;
  }
  // Retrieve the price for the selected tierId
  var tier = ticketTimeTiers.find(function (t) {
    return t.tierId === tierId;
  });
  return tier;
};
/**
 * Determines the tier ID based on session time for a given day.
 * @param day - The day configuration, including times and default tier ID.
 * @param sessionStart - The start time of the session in a 24-hour integer format.
 * @returns The tier ID matched by the time, or the default day tier ID if no specific time matches.
 */
export var getTierIdFromDate = function (day, sessionStart) {
  if (sessionStart == null || !day.times.length) {
    return day.tierId;
  }
  var specificTime = day.times.find(function (time) {
    var startTime = time.startTime;
    var endTime = time.endTime;
    var hasSession = startTime != null && endTime != null;
    return hasSession && sessionStart >= startTime && sessionStart < endTime;
  });
  return specificTime ? specificTime.tierId : day.tierId;
};
export var mapDayOfWeek = function (day) {
  var _a;
  if (typeof day === 'string') {
    return (_a = EDaysOfWeek[day]) !== null && _a !== void 0 ? _a : null;
  }
  if (typeof day === 'number') {
    return day;
  }
  return null;
};
/**
 * Determines the early bird pricing rule bracket based on the booking date and the date the booking was created.
 * @param bookingDate - The date of the booking
 * @param earlyBirdBrackets - Array of early bird pricing rule brackets
 * @param currentVenueTime - The current date and time in the venue's timezone
 * @param bookingCreatedDate - The date the booking was originally created on
 * @returns The matching early bird pricing tier based on the days between the booking date and the booking created date, or today's date if no booking created date is provided.
 */
export var getEarlyBirdPricingRuleBracket = function (bookingDate, earlyBirdBrackets, currentVenueTime, bookingCreatedDate) {
  var lastBracket = earlyBirdBrackets[earlyBirdBrackets.length - 1];
  var currentVenueDate = new Date(currentVenueTime.setHours(0, 0, 0, 0));
  var dateOfBooking = bookingCreatedDate ? new Date(bookingCreatedDate.setHours(0, 0, 0, 0)) : currentVenueDate;
  var dateDiff = bookingDate.getTime() - dateOfBooking.getTime();
  var daysFromCurrentDate = Math.ceil(dateDiff / (1000 * 60 * 60 * 24));
  if (daysFromCurrentDate >= lastBracket.from) {
    return lastBracket;
  }
  var matchingBracket = earlyBirdBrackets.find(function (bracket, index) {
    var nextBracket = earlyBirdBrackets[index + 1];
    return daysFromCurrentDate >= bracket.from && daysFromCurrentDate < nextBracket.from;
  });
  return matchingBracket;
};